import type { ILogLayer } from "loglayer";

const isServer = typeof window === "undefined";

let logger: ILogLayer | null = null;

/**
 * Get the appropriate logger instance based on the runtime environment.
 * Returns server logger when running on Node.js, client logger in the browser.
 *
 * @example
 * ```typescript
 * import { getLogger } from '~/lib/logger';
 *
 * const log = getLogger();
 *
 * // Basic logging
 * log.info('User signed in');
 *
 * // With metadata
 * log.withMetadata({ userId: '123' }).info('User action completed');
 *
 * // Error logging
 * log.withError(error).error('Authentication failed');
 * ```
 */
export function getLogger(): ILogLayer {
	if (logger) {
		return logger;
	}

	if (isServer) {
		// Dynamic import for server-side to avoid bundling issues
		const { getServerLogger } = require("./server");
		logger = getServerLogger();
	} else {
		// Dynamic import for client-side
		const { getClientLogger } = require("./client");
		logger = getClientLogger();
	}

	return logger as ILogLayer;
}

// Re-export types for convenience
export type { ILogLayer } from "loglayer";
